/**
 * InitializeRemoteConfigUseCase
 *
 * Owns the *ordering* of Flutter's `RemoteConfigService.init()`
 * (lib/services/remote_config_service.dart:110-194) as one testable domain
 * action, so the app-startup listener component supplies a single effects sink
 * and never has to know the sequence:
 *
 *  1. settings + compile-time defaults + `fetchAndActivate` / `ensureInitialized`
 *     — Flutter :112-185, delegated to `remoteConfigService.init()`;
 *  2. handle the **already-activated** template once — Flutter's
 *     `_handleConfigs()` at :189, which is nothing but `await
 *     _handleBudgetExceeded()` (:342-344), so the web expresses it as the very
 *     same handler the real-time `budgetExceeded` push uses:
 *     `handleRemoteConfigUpdateUseCase.execute([RemoteConfigs.budgetExceeded])`
 *     (one use case = one action — no second entry point for the same effect);
 *  3. subscribe to real-time updates — Flutter :191-322.
 *
 * Step 2 running **before** step 3 is load-bearing: a server template that
 * already says `budgetExceeded: true` must raise the overlay on first load,
 * without waiting for a push that may never come. Do not reorder.
 *
 * **`execute` never rejects.** It is mounted at app boot, so a Remote Config
 * failure must leave the app running on the compile-time defaults rather than
 * break startup. `remoteConfigService.init()` already swallows a failed *fetch*
 * (Flutter :179-184), but the outer try/catch additionally contains an
 * unsupported browser (`isSupported() === false`) or an IndexedDB failure.
 *
 * Idempotent by delegation: both `remoteConfigService.init()` and
 * `.subscribeToUpdates()` guard themselves, so a second `execute()` (a React
 * re-mount) neither re-fetches nor stacks a duplicate subscription. It does
 * re-emit the initial effects, which carry only the `budgetExceeded` flag and
 * an empty document list — setting that flag twice is idempotent at the
 * `appOverlay` slice, so no extra guard is added here.
 *
 * DELIBERATE DIVERGENCES from Flutter (neither is a contract, both recorded so a
 * reviewer does not read them as omissions; the Flutter source is NOT patched):
 *  - Flutter runs `init()` only on mobile (`if (!kIsWeb)`,
 *    lib/presentation/screens/root/root_screen.dart:163) because its own legacy
 *    web build has no Remote Config plugin. The React web app is precisely what
 *    this port adds Remote Config to, so no such guard exists here.
 *  - Flutter cancels the subscription on **logout**
 *    (`_cancelAppStreamSubscriptions()` → `RemoteConfigService.close()`,
 *    root_screen.dart:262) while `init()` only ever runs once at app start
 *    (root_screen.dart:163-170) — so after a re-login Flutter's listener is dead
 *    for the rest of the process. The web instead keeps the subscription for the
 *    whole page session and disposes it on listener unmount: Remote Config is
 *    app-scoped, not user-scoped, and the budget-exceeded overlay must stay
 *    reachable from the login screen. A follow-up Flutter fix is recommended.
 */

import type { RemoteConfigUpdateEffects } from '@/domain/entities/remoteConfig/remoteConfigUpdateEffects';
import { RemoteConfigs } from '@/domain/enums/remoteConfigs';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { HandleRemoteConfigUpdateUseCase } from '@/domain/usecases/remoteConfig/handleRemoteConfigUpdateUseCase';

export class InitializeRemoteConfigUseCase {
  private handleUpdate: HandleRemoteConfigUpdateUseCase;

  constructor(handleUpdate?: HandleRemoteConfigUpdateUseCase) {
    this.handleUpdate = handleUpdate || new HandleRemoteConfigUpdateUseCase();
  }

  /**
   * Runs the three init steps in Flutter's order and forwards every resulting
   * `RemoteConfigUpdateEffects` — the initial one and every later push — to the
   * caller's sink. Never rejects (see the file header).
   *
   * @param onEffects presentation sink for the UI-facing effects of a config
   *   read: the budget-exceeded overlay and the updated legal documents.
   */
  async execute(onEffects: (effects: RemoteConfigUpdateEffects) => void): Promise<void> {
    try {
      // 1. Settings + defaults + fetch/activate (remote_config_service.dart:112-185).
      await remoteConfigService.init();

      // 2. Handle the config that is already active (`_handleConfigs()`, :189 →
      //    :342-344). Must precede the subscription: a template that already
      //    reads `budgetExceeded: true` shows the overlay on first load.
      onEffects(await this.handleUpdate.execute([RemoteConfigs.budgetExceeded]));

      // 3. Real-time updates (:191). Idempotent inside the service.
      remoteConfigService.subscribeToUpdates(async (keys) => {
        await this.handlePush(keys, onEffects);
      });
    } catch (error) {
      appLogger.error(
        'Failed to initialize Remote Config; continuing on compile-time defaults',
        'InitializeRemoteConfigUseCase.execute',
        { error: error as Error }
      );
    }
  }

  /**
   * One real-time push. Runs outside `execute`'s try/catch — the SDK invokes it
   * long after `execute` resolved — so it carries its own containment: a
   * throwing sink must not surface as an unhandled rejection inside the Firebase
   * observer. `handleUpdate.execute` itself never rejects.
   */
  private async handlePush(
    keys: string[],
    onEffects: (effects: RemoteConfigUpdateEffects) => void
  ): Promise<void> {
    try {
      onEffects(await this.handleUpdate.execute(keys));
    } catch (error) {
      appLogger.error(
        'Failed to handle a Remote Config push',
        'InitializeRemoteConfigUseCase.handlePush',
        { error: error as Error }
      );
    }
  }
}

export const initializeRemoteConfigUseCase = new InitializeRemoteConfigUseCase();
